// Slim amber bar under the employee top bar: no connection, or changes waiting in the queue
// (sending, waiting for the server, or waiting for a new login); below it the persistent notices
// of queued changes the server refused.
import { AlertTriangle, LogIn, RefreshCw, WifiOff, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { offlineQueue } from "./offline-queue";
import {
  useOfflineFailures,
  useQueuedMutations,
  useQueueReplaying,
  useReplayStop,
} from "./useOfflineQueue";
import { useOfflineReplay } from "./useOfflineReplay";
import { useOnline } from "./useOnline";

export function OfflineBanner({ userId }: { userId: string }) {
  const { t } = useTranslation("translation", { keyPrefix: "pwa" });
  const { t: tToday } = useTranslation("translation", { keyPrefix: "today" });
  const online = useOnline();
  const items = useQueuedMutations();
  const failures = useOfflineFailures();
  const replaying = useQueueReplaying();
  const stop = useReplayStop();
  const replay = useOfflineReplay(userId);
  const count = items.length;
  const loginRequired = online && count > 0 && stop === "unauthorized";

  let text: string;
  if (!online) text = count > 0 ? t("offline.queued", { count }) : t("offline.noConnection");
  else if (loginRequired) text = t("offline.loginRequired", { count });
  else if (replaying) text = t("offline.sending", { count });
  else text = t("offline.waiting", { count });

  return (
    <>
      {!online || count > 0 ? (
        <div
          role="status"
          data-offline-banner
          className="flex min-h-11 items-center justify-center gap-2 bg-warning-soft px-4 py-1 text-center text-sm text-warning-strong"
        >
          {!online ? (
            <WifiOff className="size-4 shrink-0" aria-hidden />
          ) : loginRequired ? (
            <LogIn className="size-4 shrink-0" aria-hidden />
          ) : (
            <RefreshCw className={cn("size-4 shrink-0", replaying && "animate-spin")} aria-hidden />
          )}
          <span>{text}</span>
          {online && !replaying && !loginRequired ? (
            <button
              type="button"
              onClick={() => void replay()}
              className="min-h-11 shrink-0 rounded-md px-2 font-medium underline hover:bg-warning/10"
            >
              {t("offline.retry")}
            </button>
          ) : null}
        </div>
      ) : null}
      {failures.length > 0 ? (
        <ul className="flex flex-col gap-1 bg-danger-soft px-4 py-2">
          {failures.map((failure) => (
            <li
              key={failure.id}
              role="alert"
              className="flex items-start gap-2 text-danger text-sm"
            >
              <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span className="flex-1">
                {failure.reason === "foreign_user"
                  ? t("offline.droppedForeign")
                  : t("offline.failed", {
                      message:
                        failure.errorCodes.length > 0
                          ? failure.errorCodes
                              .map((code) =>
                                tToday(`ruleErrors.${code}`, { defaultValue: failure.message }),
                              )
                              .join(" ")
                          : failure.message,
                    })}
                <span className="block text-xs text-zinc-700">
                  {t(`offline.kind.${failure.kind}`)} · {failure.summary}
                </span>
              </span>
              <button
                type="button"
                aria-label={t("offline.dismiss")}
                onClick={() => void offlineQueue.dismissFailure(failure.id)}
                className="-m-1 flex size-8 shrink-0 items-center justify-center rounded-md hover:bg-danger/10"
              >
                <X className="size-4" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}
